import { v1Api } from '@/api/backendApi';

import type { BrowseType } from '@/types/browse';
import type { TracksPage } from '@/types/tracks';

interface Excluded {
  group: string;
  id: string;
}

export interface TrackOption {
  disableWeighting?: boolean;
  duration?: string | null;
  energy?: number;
  excluded?: Excluded[];
  freeOnly?: number;
  id?: string | number;
  isPremium?: boolean;
  limit?: number;
  merge?: boolean;
  page?: number;
  sort?: string;
  styleFilters?: string[];
  type?: BrowseType;
  vocal?: number;
}

const shouldFetchTopTracks = (options: Partial<TrackOption>): boolean => {
  if (
    options.page !== 1 ||
    options.sort !== 'Relevance' ||
    options.energy ||
    options.vocal ||
    options.duration ||
    (options.styleFilters?.length ?? 0) > 0 ||
    !options.isPremium ||
    options.disableWeighting
  ) {
    return false;
  }

  return true;
};

export const defaultTrackOptions: TrackOption = {
  type: '',
  id: 1,
  energy: 0,
  vocal: 0,
  sort: 'Relevance',
  duration: '0',
  styleFilters: [],
  page: 1,
  excluded: [],
  limit: -1,
  merge: false,
  freeOnly: 0,
  isPremium: false,
};

export const getAllTracks = async (
  options: Partial<TrackOption>
): Promise<TracksPage> => {
  options = { ...defaultTrackOptions, ...options };

  // Override "As Curated" to return relevancy results without changing the filter name.
  if (options.sort === 'As Curated') {
    options.sort = 'CollectionDefault';
  }

  let jsonStyleFilters;

  if (options.styleFilters != null) {
    jsonStyleFilters = JSON.stringify(options.styleFilters);
  }

  // Fetch weighted items for sets system.
  if (shouldFetchTopTracks({ ...options })) {
    const excludedJson = await v1Api
      .get<{ result_ids: Excluded[] }>(
        `tracks/${options.type}/types/${options.id}/ids/weighted`
      )
      .then((res) => res.data);

    options.excluded = excludedJson?.result_ids;
  }

  // Assign set system to array which can be used in URL.
  let jsonExcluded: number[][] | string = [];

  if (options.excluded != null) {
    for (let i = 0; i < options.excluded.length; i += 1) {
      jsonExcluded[i] = [];
      jsonExcluded[i][0] = Number.parseInt(options.excluded[i].id, 10);
      jsonExcluded[i][1] = Number.parseInt(options.excluded[i].group, 10);
    }

    jsonExcluded = JSON.stringify(jsonExcluded);
  }

  let endpoint = `tracks/${options.type}/types/${options.id}/ids/${
    options.energy
  }/energies/${options.vocal}/vocals/${options.sort}/sorts/${
    options.duration ?? 0
  }/durations/${jsonStyleFilters}/stylefilters/${
    options.limit
  }/limits/${jsonExcluded}/excludes/${options.isPremium ? '1' : '0'}/premium/${
    options.freeOnly
  }/freeonly/${options.page}`;

  // Cache busting for taxonomy.
  if (options.type === 'collection') {
    endpoint += '?version=2';
  }

  const tracks = await v1Api.get<TracksPage>(endpoint).then((res) => res.data);

  return tracks;
};
